// Surface tilt status from the performance endpoint. Shows banner only
// when level is elevated or tilt.

import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { AlertTriangle, Wind } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

export function TiltBanner() {
  const q = useQuery({ queryKey: ['performance'], queryFn: api.performance });
  // Render nothing while loading or on calm. Surface explicit fetch errors so
  // a backend hiccup doesn't masquerade as "no tilt detected" — losing the
  // most important risk signal silently.
  if (q.isError) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-[11px] text-amber-200">
        Tilt status unavailable (couldn't reach /api/performance).
      </div>
    );
  }
  const t = q.data?.tilt;
  if (!t || t.level === 'calm') return null;

  const Icon = t.level === 'tilt' ? AlertTriangle : Wind;
  return (
    <motion.div
      initial={{ opacity: 0, y: -2 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={cn(
        'rounded-xl border px-4 py-2.5 flex items-center gap-3',
        t.level === 'tilt'
          ? 'border-rose-500/40 bg-rose-500/10'
          : 'border-amber-500/40 bg-amber-500/10',
      )}
    >
      <Icon
        className={cn('size-4 shrink-0', t.level === 'tilt' ? 'text-rose-300' : 'text-amber-300')}
      />
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'text-sm font-medium',
            t.level === 'tilt' ? 'text-rose-100' : 'text-amber-100',
          )}
        >
          {t.level === 'tilt' ? 'Tilt detected' : 'Variance elevated'}
        </div>
        <div className="text-[11px] text-zinc-300">{t.message}</div>
      </div>
      <div className="text-[11px] text-zinc-400 num text-right shrink-0">
        <div>σ {t.recentSigma.toFixed(2)}%</div>
        <div className="text-zinc-400">{t.ratio.toFixed(1)}× baseline</div>
      </div>
    </motion.div>
  );
}
